/**
 * An image that does not download until the user asks, when low-data mode is on.
 *
 * Recipients and merchants are often on 3G or a metered bundle. In low-data
 * mode this renders a tappable placeholder instead of fetching; tapping loads
 * that one image and it stays loaded.
 *
 * DEFERRED, NEVER HIDDEN. A delivery proof photo is the evidence that goods
 * arrived and the basis of any dispute — removing it to save bandwidth would
 * break the thing the product exists to do. The user can always reach it in one
 * tap, and the placeholder says what it is rather than showing a broken frame.
 *
 * With low-data mode off this is an ordinary <img>, so it is safe to use
 * everywhere without branching at each call site.
 */
import { useState } from 'react';
import { ImageIcon } from 'lucide-react';
import { useLowData } from '../../contexts/LowDataContext';

interface Props {
  src: string;
  alt: string;
  className?: string;
  /** Shown on the placeholder, e.g. "Delivery photo". */
  label?: string;
}

export function DeferredImage({ src, alt, className, label = 'Photo' }: Props) {
  const { lowData } = useLowData();
  const [loadAnyway, setLoadAnyway] = useState(false);

  const shouldDefer = lowData && !loadAnyway;

  if (!shouldDefer) {
    return <img src={src} alt={alt} className={className} loading="lazy" />;
  }

  return (
    <button
      type="button"
      onClick={(e) => {
        // The image is often inside a link to the full-size version; loading it
        // in place should not also navigate.
        e.preventDefault();
        e.stopPropagation();
        setLoadAnyway(true);
      }}
      aria-label={`Load ${label.toLowerCase()}`}
      className={`${className ?? ''} flex flex-col items-center justify-center gap-1 border border-dashed border-slate-300 bg-slate-50 text-slate-500 hover:border-brand-400 hover:text-brand-700`}
    >
      <ImageIcon className="w-5 h-5" aria-hidden="true" />
      <span className="text-[10px] font-semibold leading-tight text-center px-1">
        Tap to load
      </span>
    </button>
  );
}
